import { useState } from 'react';
import {
  EDP_AX_ID,
  PHILOSOPHIES,
  DEVICE_LAYOUTS,
  CONTINUITY_FIELDS,
  ACCESSIBILITY_STANDARDS,
  DENSITY_MODES,
  NOTIFICATION_CHANNELS,
  PERSONALIZATION_OPTIONS,
  MOBILE_WORKFLOWS,
  TOUCH_TARGET_MIN_PX,
  buildAdaptiveDashboard,
} from '../../adaptivexp/framework';
import { BREAKPOINTS } from '../../designsystem/tokens';
import {
  DevicePreviewFrame,
  AccessibilityChecklist,
  ContinuityBanner,
  DensityModeToggle,
  OfflineIndicator,
} from '../../components/adaptivexp';
import { Card, KpiCard } from '../../components/ui';
import type { DensityMode } from '../../adaptivexp/framework';

export function AdaptiveExperiencePage() {
  const dashboard = buildAdaptiveDashboard();
  const [density, setDensity] = useState<DensityMode>('Comfortable');

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{EDP_AX_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Adaptive Experience Framework</h1>
        <p className="mt-1 text-sm text-slate-500">
          Responsive architecture, accessibility, personalization, and cross-device continuity — one workflow, many devices.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Device Types" value={dashboard.device_types.toString()} />
        <KpiCard label="Continuity Fields" value={dashboard.continuity_fields.toString()} />
        <KpiCard label="A11y Standards" value={dashboard.accessibility_standards.toString()} />
        <KpiCard label="Touch Target Min" value={`${TOUCH_TARGET_MIN_PX}px`} />
      </div>

      <ContinuityBanner device="Laptop" syncedFields={['Context', 'Filters', 'AI Conversation', 'Workspace Layout']} />

      <section>
        <h2 className="text-lg font-semibold">Adaptive Philosophy</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {PHILOSOPHIES.map((p) => (
            <Card key={p.id} padding="sm">
              <p className="font-medium">{p.label}</p>
              <p className="mt-1 text-xs text-slate-500">{p.description}</p>
            </Card>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Device Layout Preview</h2>
        <p className="mt-1 text-sm text-slate-500">Panel visibility adapts per form factor (EDP-00007 §4).</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {DEVICE_LAYOUTS.map((spec) => (
            <DevicePreviewFrame key={spec.device} spec={spec} />
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Design Token Breakpoints</h2>
          <Card className="mt-4" padding="sm">
            <dl className="space-y-2 text-sm">
              {Object.entries(BREAKPOINTS).map(([name, px]) => (
                <div key={name} className="flex justify-between">
                  <dt className="capitalize text-slate-600 dark:text-slate-400">{name}</dt>
                  <dd className="font-mono text-slate-900 dark:text-slate-100">{px}px</dd>
                </div>
              ))}
            </dl>
          </Card>
        </div>
        <AccessibilityChecklist standards={ACCESSIBILITY_STANDARDS.map((s) => `${s} — compliant`)} />
      </section>

      <section>
        <h2 className="text-lg font-semibold">Cross-Device Continuity</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {CONTINUITY_FIELDS.map((f) => (
            <span key={f} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">
              {f}
            </span>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Density Mode</h2>
          <DensityModeToggle value={density} onChange={setDensity} className="mt-3" />
          <p className="mt-2 text-xs text-slate-500">Modes: {DENSITY_MODES.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Notification Channels</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {NOTIFICATION_CHANNELS.map((c) => (
              <span key={c} className="rounded-full bg-slate-100 px-3 py-1 text-xs dark:bg-slate-800">{c}</span>
            ))}
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Mobile-Optimized Workflows</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {MOBILE_WORKFLOWS.map((w) => (
            <span key={w} className="rounded-md bg-brand-50 px-2 py-1 text-xs text-brand-700 dark:bg-brand-950 dark:text-brand-300">
              {w}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Personalization Options</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {PERSONALIZATION_OPTIONS.map((o) => (
            <span key={o} className="text-xs text-slate-600 dark:text-slate-400">• {o}</span>
          ))}
        </div>
      </section>

      <OfflineIndicator queuedActions={2} />

      <Card className="border-brand-200 bg-brand-50/30 dark:border-brand-900 dark:bg-brand-950/20">
        <p className="text-sm font-medium">Developer Implementation</p>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          Registry: <code className="rounded bg-white/60 px-1 dark:bg-slate-800">pkg/adaptivexp/</code> ·
          Tokens: <code className="rounded bg-white/60 px-1 dark:bg-slate-800">designsystem/tokens.ts</code> ·
          Theme: <code className="rounded bg-white/60 px-1 dark:bg-slate-800">designsystem/theme.ts</code>
        </p>
        <p className="mt-2 text-xs text-slate-500">
          Spec: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">docs/edp/EDP-00007_ADAPTIVE_EXPERIENCE.md</code>
        </p>
      </Card>
    </div>
  );
}
